import {PropsWithChildren, useCallback, useMemo} from 'react';
import {useDocumentContext} from '@/components/document/context';
import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {DocumentBaseGroup, DocumentField, DocumentGroupModalButton} from '@/engine';
import {useWebViewContentOnDialogAppear} from '@/hooks/analytics/use-web-view-content-on-dialog-appear';
import {getAnalyticsWebViewContentDetail} from '@/services/analytics/get-analytics-web-view-content-detail';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import Button from '../button/button';
import Dialog from '../dialog';
import {useDocumentLocale} from './hooks';

type Props = PropsWithChildren<{
  group: DocumentBaseGroup;
  onClose: () => void;
}>;

export type GroupModalProps = {
  group: DocumentBaseGroup;
  fields: DocumentField[];
  sectionId: string;
};

export type GroupNewActionsProps = {
  onSave: () => void;
  onCancel: () => void;
  isSaveDisabled?: boolean;
  onSaveDisabled?: () => void;
};

export type GroupEditActionsProps = GroupNewActionsProps & {
  onDelete: () => void;
};

export type GroupCustomActionsProps = {
  buttons: DocumentGroupModalButton[];
};

export const GroupModalWrapper = ({group, onClose, children}: Props) => {
  const {valueResolver, visibilitySet} = useDocumentContext();

  const isVisible = valueResolver({$ref: `_${group.id}[open]`}, {transform: value => value === 'true'});

  useWebViewContentOnDialogAppear('wrapper-groupe-modale-contenu', getAnalyticsWebViewContentDetail(group), isVisible);

  return (
    <Dialog isVisible={isVisible} onClose={onClose} panelClassName="max-w-7xl" sharedBackdrop>
      <Dialog.Header title={valueResolver(group.title, {visibilitySet})} onClose={onClose} />
      <Dialog.Content>{children}</Dialog.Content>
    </Dialog>
  );
};

export const GroupModalEditActions = ({
  onSave,
  onCancel,
  onDelete,
  isSaveDisabled,
  onSaveDisabled,
}: GroupEditActionsProps) => {
  const {t} = useDocumentLocale();

  const handleOnSave = useCallback(() => {
    if (!isSaveDisabled) return onSave();
    return onSaveDisabled?.();
  }, [isSaveDisabled, onSave, onSaveDisabled]);

  const allowClickOnDisabled = useMemo(() => Boolean(onSaveDisabled), [onSaveDisabled]);

  return (
    <div className="flex items-center justify-between gap-5">
      <Button
        theme={BUTTON_THEME.DANGER}
        variant={BUTTON_VARIANT.SMALL}
        label={t('components.group-entry-dialog.edit.delete')}
        trackingKey={WEB_CLICK_TRACKING_KEY.DELETE_GROUP_ENTRY}
        data-testid="dialog-edit-delete-button"
        onClick={onDelete}
      />
      <div className="flex items-center gap-5">
        <Button
          theme={BUTTON_THEME.TERTIARY}
          variant={BUTTON_VARIANT.SMALL}
          label={t('components.group-entry-dialog.edit.cancel')}
          trackingKey={WEB_CLICK_TRACKING_KEY.CANCEL_GROUP_EDIT}
          data-testid="dialog-edit-cancel-button"
          onClick={onCancel}
        />
        <Button
          theme={BUTTON_THEME.PRIMARY}
          variant={BUTTON_VARIANT.SMALL}
          label={t('components.group-entry-dialog.edit.save')}
          trackingKey={WEB_CLICK_TRACKING_KEY.SAVE_GROUP_EDIT}
          data-testid="dialog-edit-save-button"
          onClick={handleOnSave}
          disabled={isSaveDisabled}
          allowClickOnDisabled={allowClickOnDisabled}
        />
      </div>
    </div>
  );
};

export const GroupModalNewActions = ({onSave, onCancel, isSaveDisabled, onSaveDisabled}: GroupNewActionsProps) => {
  const {t} = useDocumentLocale();

  const handleOnSave = useCallback(() => {
    if (!isSaveDisabled) return onSave();
    return onSaveDisabled?.();
  }, [isSaveDisabled, onSave, onSaveDisabled]);

  const allowClickOnDisabled = useMemo(() => Boolean(onSaveDisabled), [onSaveDisabled]);

  return (
    <div className="flex items-center justify-end gap-5">
      <Button
        theme={BUTTON_THEME.TERTIARY}
        variant={BUTTON_VARIANT.SMALL}
        label={t('components.group-entry-dialog.new.cancel')}
        trackingKey={WEB_CLICK_TRACKING_KEY.CANCEL_NEW_GROUP}
        data-testid="dialog-new-cancel-button"
        onClick={onCancel}
      />
      <Button
        theme={BUTTON_THEME.PRIMARY}
        variant={BUTTON_VARIANT.SMALL}
        label={t('components.group-entry-dialog.new.save')}
        trackingKey={WEB_CLICK_TRACKING_KEY.ADD_GROUP_ENTRY}
        data-testid="dialog-new-save-button"
        onClick={handleOnSave}
        disabled={isSaveDisabled}
        allowClickOnDisabled={allowClickOnDisabled}
      />
    </div>
  );
};

export const GroupModalCustomActions = ({buttons}: GroupCustomActionsProps) => {
  const {_t} = useDocumentLocale();

  if (buttons.length === 0) return null;

  return (
    <div className="flex items-center justify-end gap-5">
      {buttons.map((button, index) => (
        <Button
          key={`${button.trackingKey}-${index}`}
          theme={button.theme ?? BUTTON_THEME.PRIMARY}
          variant={BUTTON_VARIANT.SMALL}
          label={_t(button.label)}
          trackingKey={button.trackingKey}
          onClick={() => button.onClick.forEach(action => action())}
          data-testid={`dialog-custom-button-${index}`}
        />
      ))}
    </div>
  );
};
